import { apiClient } from '@/shared/lib/axios';
import { UserId } from '@/domain/value-objects/UserId';
import { SiteId } from '@/domain/value-objects/SiteId';
import type {
  LoginRequest,
  LoginResponse,
  User,
  UserApiResponse,
  VerifyTwoFactorRequest,
  VerifyTwoFactorResponse,
  ResendTwoFactorResponse,
} from '../types/auth.types';

const AUTH_API_PATH = '/api/auth';

interface CsrfTokenResponse {
  csrfToken: string;
}

interface SessionResponse {
  user: UserApiResponse;
  message: string;
}

// ログインAPIレスポンス（2FA対応）
interface LoginApiResponse {
  user?: UserApiResponse;
  requiresTwoFactor?: boolean;
  maskedEmail?: string;
  expiresAt?: string;
  debugCode?: string; // STG環境でのみ設定
}

// 2FA検証APIレスポンス（プリミティブ型）
interface VerifyTwoFactorApiResponse {
  user: UserApiResponse;
}

/**
 * APIレスポンスからドメインモデルへの変換
 * UI層でのnew禁止ルールに従い、ドメインオブジェクトはここで生成
 */
const convertApiResponseToUser = (apiUser: UserApiResponse): User => {
  return {
    id: UserId.create(apiUser.id),
    userName: apiUser.userName,
    roleId: apiUser.roleId,
    siteId: apiUser.siteId !== null && apiUser.siteId !== undefined ? SiteId.create(apiUser.siteId) : undefined,
    role: apiUser.role,
    site: apiUser.site
      ? {
          id: SiteId.create(apiUser.site.id),
          siteName: apiUser.site.siteName,
        }
      : null,
  };
};

export const authService = {
  /**
   * CSRFトークンを取得
   */
  getCsrfToken: async (): Promise<string> => {
    const response = await apiClient.get<CsrfTokenResponse>(
      `${AUTH_API_PATH}/csrf-token`
    );
    return response.data.csrfToken;
  },

  /**
   * ユーザーログイン（2FA対応・値オブジェクト変換あり）
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    // まずCSRFトークンを取得
    const csrfToken = await authService.getCsrfToken();

    const response = await apiClient.post<LoginApiResponse>(
      `${AUTH_API_PATH}/login`,
      credentials,
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    // 2FA必要な場合: userは含まれないため変換不要
    // debugCode は STG のみ API が返す。落とすと 2FA 画面に認証コードが表示されない
    if (response.data.requiresTwoFactor === true) {
      return {
        requiresTwoFactor: true,
        maskedEmail: response.data.maskedEmail,
        expiresAt: response.data.expiresAt,
        ...(response.data.debugCode !== undefined && { debugCode: response.data.debugCode }),
      };
    }

    // 通常ログイン: APIレスポンスをドメインモデル（値オブジェクト）に変換
    if (response.data.user === undefined) {
      throw new Error('Unexpected response: user is undefined');
    }

    return {
      user: convertApiResponseToUser(response.data.user),
    };
  },

  /**
   * ユーザーログアウト
   */
  logout: async (): Promise<void> => {
    // CSRFトークンを取得してログアウト
    const csrfToken = await authService.getCsrfToken();
    await apiClient.post(
      `${AUTH_API_PATH}/logout`,
      {},
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );
  },

  /**
   * 現在のセッション情報を取得
   */
  getSession: async (): Promise<User> => {
    const response = await apiClient.get<SessionResponse>(
      `${AUTH_API_PATH}/session`
    );
    // APIレスポンスをドメインモデルに変換
    return convertApiResponseToUser(response.data.user);
  },

  /**
   * トークンの検証（セッションチェック）
   */
  verifyToken: async (): Promise<boolean> => {
    try {
      await authService.getSession();
      return true;
    } catch {
      return false;
    }
  },

  /**
   * 2FAコード検証（値オブジェクト変換あり）
   * pendingTokenはHttpOnly Cookieから自動送信される
   */
  verifyTwoFactor: async (params: VerifyTwoFactorRequest): Promise<VerifyTwoFactorResponse> => {
    const csrfToken = await authService.getCsrfToken();

    const response = await apiClient.post<VerifyTwoFactorApiResponse>(
      `${AUTH_API_PATH}/verify-2fa`,
      { code: params.code },
      {
        headers: { 'x-csrf-token': csrfToken },
        withCredentials: true,
      }
    );

    // APIレスポンスをドメインモデルに変換
    return {
      user: convertApiResponseToUser(response.data.user),
    };
  },

  /**
   * 2FAコード再送信
   * pendingTokenはHttpOnly Cookieから自動送信される
   */
  resendTwoFactorCode: async (): Promise<ResendTwoFactorResponse> => {
    const csrfToken = await authService.getCsrfToken();

    const response = await apiClient.post<ResendTwoFactorResponse>(
      `${AUTH_API_PATH}/resend-2fa`,
      {},
      {
        headers: { 'x-csrf-token': csrfToken },
        withCredentials: true,
      }
    );

    return response.data;
  },
};